import { useState, useEffect } from 'react';
import { Phone, PhoneCall, PhoneIncoming, PhoneOutgoing, Clock, Calendar, Play } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getCallRecording } from '../../services/ringcentralService';

interface CallRecord {
  id: string;
  ringcentral_call_id: string;
  recruiter_email: string;
  recruiter_extension: string;
  call_type: string;
  direction: 'Inbound' | 'Outbound';
  call_result: string;
  from_phone: string;
  to_phone: string;
  start_time: string;
  duration: number;
  has_recording: boolean;
  recording_id: string | null;
  transcript: string | null;
  transcript_status: string | null;
  recording_uri: string | null;
}

interface CallHistoryWidgetProps {
  driverId?: string;
  candidatePhone?: string;
  compact?: boolean;
}

export function CallHistoryWidget({ driverId, candidatePhone, compact = false }: CallHistoryWidgetProps) {
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingRecording, setPlayingRecording] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalCalls: 0,
    lastContact: null as string | null,
    totalDuration: 0,
  });

  useEffect(() => {
    loadCallHistory();
  }, [driverId, candidatePhone]);

  const loadCallHistory = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('recruiter_calls')
        .select('*')
        .order('start_time', { ascending: false });

      if (driverId) {
        query = query.eq('driver_id', driverId);
      } else if (candidatePhone) {
        query = query.eq('candidate_phone', candidatePhone);
      } else {
        setLoading(false);
        return;
      }

      const { data, error } = await query;

      if (error) throw error;

      setCalls(data || []);

      // Calculate stats
      if (data && data.length > 0) {
        setStats({
          totalCalls: data.length,
          lastContact: data[0].start_time,
          totalDuration: data.reduce((sum, call) => sum + (call.duration || 0), 0),
        });
      }
    } catch (error) {
      console.error('Error loading call history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const handlePlayRecording = async (recordingId: string) => {
    try {
      setPlayingRecording(recordingId);
      const blob = await getCallRecording(recordingId);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.play();
      audio.onended = () => {
        setPlayingRecording(null);
        URL.revokeObjectURL(url);
      };
    } catch (error) {
      console.error('Error playing recording:', error);
      setPlayingRecording(null);
    }
  };

  const getResultColor = (result: string): string => {
    switch (result) {
      case 'Accepted':
        return 'text-green-600 bg-green-50';
      case 'Missed':
        return 'text-red-600 bg-red-50';
      case 'Voicemail':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-slate-600 bg-slate-50';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded w-1/3"></div>
          <div className="h-16 bg-slate-200 rounded"></div>
          <div className="h-16 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Phone className="w-4 h-4 text-blue-600" />
            Call History
          </h3>
          {stats.lastContact && (
            <span className="text-xs text-slate-500">
              Last: {formatDate(stats.lastContact)}
            </span>
          )}
        </div>

        {calls.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No calls recorded</p>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-blue-50 rounded p-2 text-center">
                <div className="font-semibold text-blue-900">{stats.totalCalls}</div>
                <div className="text-blue-600">Total Calls</div>
              </div>
              <div className="bg-green-50 rounded p-2 text-center">
                <div className="font-semibold text-green-900">
                  {calls.filter(c => c.call_result === 'Accepted' || c.call_result === 'Call connected').length}
                </div>
                <div className="text-green-600">Answered</div>
              </div>
              <div className="bg-purple-50 rounded p-2 text-center">
                <div className="font-semibold text-purple-900">{formatDuration(stats.totalDuration)}</div>
                <div className="text-purple-600">Talk Time</div>
              </div>
            </div>

            <div className="max-h-48 overflow-y-auto space-y-1">
              {calls.slice(0, 5).map((call) => (
                <div key={call.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-xs hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-2 flex-1">
                    {call.direction === 'Outbound' ? (
                      <PhoneOutgoing className="w-3 h-3 text-blue-600" />
                    ) : (
                      <PhoneIncoming className="w-3 h-3 text-green-600" />
                    )}
                    <span className="text-slate-700">{formatDate(call.start_time)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${getResultColor(call.call_result)}`}>
                      {call.call_result}
                    </span>
                    {call.duration > 0 && (
                      <span className="text-slate-500">{formatDuration(call.duration)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-blue-600" />
            Call History
          </h3>
          {stats.lastContact && (
            <span className="text-sm text-slate-600">
              Last contacted: {formatDate(stats.lastContact)}
            </span>
          )}
        </div>

        {calls.length > 0 && (
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-blue-900">{stats.totalCalls}</div>
              <div className="text-sm text-blue-600">Total Calls</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-900">
                {calls.filter(c => c.call_result === 'Accepted' || c.call_result === 'Call connected').length}
              </div>
              <div className="text-sm text-green-600">Answered</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-orange-900">
                {calls.filter(c => c.call_result === 'Missed').length}
              </div>
              <div className="text-sm text-orange-600">Missed</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-purple-900">{formatDuration(stats.totalDuration)}</div>
              <div className="text-sm text-purple-600">Total Talk Time</div>
            </div>
          </div>
        )}
      </div>

      <div className="p-4">
        {calls.length === 0 ? (
          <div className="text-center py-8">
            <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No call history available</p>
            <p className="text-sm text-slate-400 mt-1">Calls will appear here once synced from RingCentral</p>
          </div>
        ) : (
          <div className="space-y-2">
            {calls.map((call) => (
              <div
                key={call.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className={`p-2 rounded-lg ${call.direction === 'Outbound' ? 'bg-blue-100' : 'bg-green-100'}`}>
                    {call.direction === 'Outbound' ? (
                      <PhoneOutgoing className="w-4 h-4 text-blue-600" />
                    ) : (
                      <PhoneIncoming className="w-4 h-4 text-green-600" />
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">
                        {call.direction === 'Outbound' ? call.to_phone : call.from_phone}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getResultColor(call.call_result)}`}>
                        {call.call_result}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(call.start_time)}
                      </span>
                      {call.duration > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDuration(call.duration)}
                        </span>
                      )}
                      <span className="text-xs text-slate-500">
                        by {call.recruiter_email.split('@')[0]} (Ext {call.recruiter_extension})
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {call.has_recording && call.recording_id && (
                    <button
                      onClick={() => handlePlayRecording(call.recording_id!)}
                      disabled={playingRecording === call.recording_id}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Play recording"
                    >
                      {playingRecording === call.recording_id ? (
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
